import { LOCAL_STORAGE } from "@/constants";
import { User } from "@/types/user";
import { Workspace } from "@/types/workspaces";

interface ResolveActiveWorkspaceIdParams {
  workspaces: Workspace[];
  storedWorkspaceId?: number | null;
  defaultWorkspaceId?: number | null;
  organizationId?: number | null;
}

const toValidWorkspaceId = (value: unknown): number | null => {
  const workspaceId = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(workspaceId) || workspaceId <= 0) {
    return null;
  }
  return workspaceId;
};

export const getInitialActiveWorkspaceId = (): number | null => {
  const storedWorkspaceId = localStorage.getItem(LOCAL_STORAGE.ACTIVE_WORKSPACE_ID);
  return toValidWorkspaceId(storedWorkspaceId);
};

export const persistActiveWorkspaceId = (workspaceId: number): void => {
  const validWorkspaceId = toValidWorkspaceId(workspaceId);
  if (!validWorkspaceId) {
    return;
  }
  localStorage.setItem(LOCAL_STORAGE.ACTIVE_WORKSPACE_ID, validWorkspaceId.toString());
};

export const getUserDefaultWorkspaceId = (
  user?: Pick<User, "default_organization_id" | "memberships"> | null,
): number | null => {
  if (!user) {
    return null;
  }

  const defaultOrganizationId = user.default_organization_id;
  const defaultWorkspaceId = user.memberships?.organizations?.find(
    organization => organization.organization_id === defaultOrganizationId,
  )?.default_workspace_id;

  return toValidWorkspaceId(defaultWorkspaceId);
};

export const resolveActiveWorkspaceId = ({
  workspaces,
  storedWorkspaceId = null,
  defaultWorkspaceId = null,
  organizationId = null,
}: ResolveActiveWorkspaceIdParams): number | null => {
  const eligibleWorkspaces = organizationId
    ? workspaces.filter(workspace => workspace.organization_id === organizationId)
    : workspaces;

  if (eligibleWorkspaces.length === 0) {
    return null;
  }

  const validStoredWorkspaceId = toValidWorkspaceId(storedWorkspaceId);
  if (
    validStoredWorkspaceId &&
    eligibleWorkspaces.some(workspace => workspace.id === validStoredWorkspaceId)
  ) {
    return validStoredWorkspaceId;
  }

  const validDefaultWorkspaceId = toValidWorkspaceId(defaultWorkspaceId);
  if (
    validDefaultWorkspaceId &&
    eligibleWorkspaces.some(workspace => workspace.id === validDefaultWorkspaceId)
  ) {
    return validDefaultWorkspaceId;
  }

  const defaultWorkspace = eligibleWorkspaces.find(workspace => workspace.is_default);
  if (defaultWorkspace) {
    return defaultWorkspace.id;
  }

  return eligibleWorkspaces[0]?.id ?? null;
};
